import React, { useState, useRef, useEffect } from 'react';
import './AIAssistant.css';

const AI_BASE_URL = 'https://et-pos.store/connector/api/ai-assistant/generate';

const QUICK_PROMPTS = [
  "Write a product description for my store",
  "Write a social media post for my business",
  "Write a review response for a customer complaint",
  "Write a professional business email to a customer",
  "Write a business proposal for a new client",
  "Write a google ads copy for my product",
];

const TABS = [
  { id: 'chat', label: '💬 Chat' },
  { id: 'insights', label: '📝 Content' },
  { id: 'stock', label: '📧 Email' },
];

async function callLaravelGenerate(tool, body) {
  const token = localStorage.getItem('etpos_token');

  const response = await fetch(`${AI_BASE_URL}/${tool}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || `Request failed: ${response.status}`);
  return data;
}

function SecureAIAssistant() {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [messages, setMessages] = useState([
    {
      role: 'ai',
      content: "Hello! I'm your secure AI assistant. Your data stays on your server. Ask me about sales, products, or inventory.",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState('');
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [stockSuggestions, setStockSuggestions] = useState('');
  const [stockLoading, setStockLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && activeTab === 'chat' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open, activeTab]);

  const detectToolAndPayload = (msg) => {
    const m = msg.toLowerCase();
    if (m.includes('email') || m.includes('write mail') || m.includes('send mail'))
      return { tool: 'email', body: { sender: 'Business Owner', recipient: 'Customer', email_about: msg, tone: 'professional', language: 'English' } };
    if (m.includes('social') || m.includes('instagram') || m.includes('facebook post') || m.includes('tweet') || m.includes('post'))
      return { tool: 'social_post', body: { description: msg, language: 'English' } };
    if (m.includes('google ad') || m.includes('google ads'))
      return { tool: 'google-ads', body: { name: 'My Business', description: msg, language: 'English' } };
    if (m.includes('facebook ad') || m.includes('fb ad'))
      return { tool: 'fb-ads', body: { name: 'My Business', description: msg, language: 'English' } };
    if (m.includes('review response') || m.includes('respond to review') || m.includes('reply to review'))
      return { tool: 'review_response', body: { customer_review: msg, language: 'English' } };
    if (m.includes('product review') || m.includes('write review'))
      return { tool: 'product_review', body: { name: 'Product', description: msg, features_liked: msg, language: 'English' } };
    if (m.includes('product description') || m.includes('brand description') || m.includes('describe product'))
      return { tool: 'brandproduct-descriptions', body: { name: 'Product', description: msg, language: 'English' } };
    if (m.includes('proposal') || m.includes('business proposal') || m.includes('client proposal'))
      return { tool: 'proposal', body: { what_biz_does: msg, what_do_for_client: msg, language: 'English' } };
    // Default: kb for general questions
    return { tool: 'kb', body: { kb_details: msg, language: 'English' } };
  };

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);

    try {
      const { tool, body } = detectToolAndPayload(msg);
      const data = await callLaravelGenerate(tool, body);
      const reply = data.result || data.response || data.output || 'No response received.';
      setMessages(prev => [...prev, { role: 'ai', content: reply }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'error', content: `Error: ${err.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  const loadInsights = async () => {
    setInsightsLoading(true);
    setInsights('');
    try {
      // Use Laravel proposal endpoint to generate business insights
      const data = await callLaravelGenerate('proposal', {
        what_biz_does: 'We are a business selling products and managing inventory.',
        what_do_for_client: 'Provide a detailed business performance insights, sales analysis, and growth recommendations.',
        language: 'English',
      });
      setInsights(data.result || data.response || data.output || 'No insights available.');
    } catch (err) {
      setInsights(`Failed to load insights: ${err.message}`);
    } finally {
      setInsightsLoading(false);
    }
  };

  const loadEmailDraft = async () => {
    setStockLoading(true);
    setStockSuggestions('');
    try {
      // Use Laravel email endpoint to draft a professional business email
      const data = await callLaravelGenerate('email', {
        sender: 'Business Owner',
        recipient: 'Valued Customer',
        email_about: 'Following up on recent purchases and offering assistance with any questions or concerns.',
        tone: 'professional',
        language: 'English',
      });
      setStockSuggestions(data.result || data.response || data.output || 'No email draft available.');
    } catch (err) {
      setStockSuggestions(`Failed to generate email: ${err.message}`);
    } finally {
      setStockLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating toggle button */}
      <button
        className="ai-float-btn"
        onClick={() => setOpen(prev => !prev)}
        title="AI Assistant"
        aria-label="Open AI Assistant"
      >
        <span className="ai-float-pulse" />
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="10" r="1" fill="currentColor" />
            <circle cx="8" cy="10" r="1" fill="currentColor" />
            <circle cx="16" cy="10" r="1" fill="currentColor" />
          </svg>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="ai-assistant-overlay">
          {/* Header */}
          <div className="ai-header">
            <div className="ai-header-icon">🔒</div>
            <div className="ai-header-info">
              <div className="ai-header-title">AI Assistant</div>
              <div className="ai-header-subtitle">Secure · Powered by your server</div>
            </div>
            <button className="ai-header-close" onClick={() => setOpen(false)} aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="ai-tabs">
            {TABS.map(tab => (
              <button
                key={tab.id}
                className={`ai-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab: Chat */}
          {activeTab === 'chat' && (
            <>
              <div className="ai-messages">
                {messages.map((msg, i) => (
                  <div key={i} className={`ai-msg ${msg.role}`}>
                    {msg.content}
                  </div>
                ))}
                {loading && (
                  <div className="ai-typing">
                    <span /><span /><span />
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="ai-quick-prompts">
                {QUICK_PROMPTS.map((p, i) => (
                  <button
                    key={i}
                    className="ai-quick-chip"
                    onClick={() => sendMessage(p)}
                    disabled={loading}
                  >
                    {p}
                  </button>
                ))}
              </div>

              <div className="ai-input-area">
                <textarea
                  ref={inputRef}
                  className="ai-input"
                  placeholder="Ask anything about your business..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  disabled={loading}
                />
                <button
                  className="ai-send-btn"
                  onClick={() => sendMessage()}
                  disabled={loading || !input.trim()}
                  aria-label="Send"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" />
                  </svg>
                </button>
              </div>
            </>
          )}

          {/* Tab: Insights */}
          {activeTab === 'insights' && (
            <div className="ai-insights-panel">
              <button className="ai-action-btn" onClick={loadInsights} disabled={insightsLoading}>
                {insightsLoading ? '⏳ Analyzing sales data...' : '🔍 Generate Sales Insights'}
              </button>
              {insights ? (
                <div className="ai-insights-text" dangerouslySetInnerHTML={{ __html: insights }} />
              ) : (
                !insightsLoading && (
                  <div className="ai-insight-placeholder">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                    <p>Click above to analyze your sales data and get AI-powered business insights.</p>
                  </div>
                )
              )}
            </div>
          )}

          {/* Tab: Email */}
          {activeTab === 'stock' && (
            <div className="ai-insights-panel">
              <button className="ai-action-btn" onClick={loadEmailDraft} disabled={stockLoading}>
                {stockLoading ? '⏳ Drafting email...' : '� Generate Customer Email'}
              </button>
              {stockSuggestions ? (
                <div className="ai-insights-text" dangerouslySetInnerHTML={{ __html: stockSuggestions }} />
              ) : (
                !stockLoading && (
                  <div className="ai-insight-placeholder">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    <p>Click above to generate a professional customer email using your Laravel AI.</p>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default SecureAIAssistant;
